import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/LanguageContext';

// Küçük (dust) bakiyeler 2 ondalıkla "0,00" görünüp yokmuş gibi durmasın diye miktara göre
// ondalık hassasiyeti artan bir biçimlendirme — 1'den küçükse 8, 100'den küçükse 4, değilse 2.
function fmtAsset(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const decimals = abs === 0 ? 2 : abs < 1 ? 8 : abs < 100 ? 4 : 2;
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: decimals });
}

function fmtUsdt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Fon ve İşlem hesabındaki aynı para birimini TEK bir toplam altında birleştirir (kullanıcı artık
// "hangi hesapta" değil "elimde ne var" görmek istiyor) — sadece GERÇEKTEN "0"dan farklı olanlar
// listelenir (küsürat kadar bile olsa; tam "0" olanlar hiç gösterilmez).
function mergeAssets(funding, trading) {
  const map = new Map();
  for (const b of [...(funding || []), ...(trading || [])]) {
    const prev = map.get(b.ccy) || { ccy: b.ccy, amount: 0, usdValue: 0 };
    prev.amount   += b.amount || 0;
    prev.usdValue += b.usdValue || 0;
    map.set(b.ccy, prev);
  }
  return [...map.values()]
    .filter(b => b.amount > 0)
    .sort((a, b) => (b.usdValue || 0) - (a.usdValue || 0));
}

function findBal(list, ccy) {
  return list?.find(b => b.ccy === ccy)?.amount ?? 0;
}

export default function WalletTransfer({ token }) {
  const { t } = useTranslation();
  const [balances, setBalances] = useState(null); // { funding: [{ccy,amount,usdValue}], trading: [...] }
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [balanceError, setBalanceError] = useState(null);

  // USDT ⇄ TRY dönüşümü — miktar, kaynak bakiyenin YÜZDESİ olarak bir slider ile seçilir (serbest
  // metin girişi yerine — "ne kadar dönüştürmek istediğini seçmek" daha şık/az hatalı).
  const [convertDirection, setConvertDirection] = useState('try_to_usdt'); // ya da 'usdt_to_try'
  const [convertPct, setConvertPct] = useState(0); // 0-100
  const [converting, setConverting] = useState(false);
  const [convertMsg, setConvertMsg] = useState(null);

  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const loadBalances = useCallback(() => {
    setLoadingBalances(true);
    setBalanceError(null);
    fetch('/api/wallet/balances', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || t('walletTransfer.balancesFailed'));
        setBalances(d);
      })
      .catch(err => setBalanceError(err.message))
      .finally(() => setLoadingBalances(false));
  }, [token]);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  const loadDetail = useCallback(() => {
    setLoadingDetail(true);
    setDetailError(null);
    fetch('/api/wallet/diagnostics', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || t('walletTransfer.detailFailed'));
        setDetail(d);
      })
      .catch(err => setDetailError(err.message))
      .finally(() => setLoadingDetail(false));
  }, [token]);

  const toggleDetail = () => {
    setShowDetail(v => {
      const next = !v;
      if (next && !detail) loadDetail();
      return next;
    });
  };

  const isTryToUsdt = convertDirection === 'try_to_usdt';
  const tradingUsdt = findBal(balances?.trading, 'USDT');
  const tradingTry  = findBal(balances?.trading, 'TRY');
  const sourceBalance  = isTryToUsdt ? tradingTry : tradingUsdt;
  const convertAmount  = (convertPct / 100) * sourceBalance;

  const flipConvertDirection = () => {
    if (converting) return;
    setConvertDirection(d => (d === 'try_to_usdt' ? 'usdt_to_try' : 'try_to_usdt'));
    setConvertPct(0); // yön değişince kaynak bakiye değişir — yüzdeyi sıfırla, yanlışlıkla eski %'yi yeni kaynağa uygulama
  };

  const handleConvert = async () => {
    if (!convertAmount || convertAmount <= 0) { setConvertMsg({ type: 'error', text: t('walletTransfer.invalidAmount') }); return; }
    setConverting(true);
    setConvertMsg(null);
    try {
      const res = await fetch('/api/wallet/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: convertAmount, direction: convertDirection }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('walletTransfer.convertFailed'));
      setConvertMsg({ type: 'ok', text: t('walletTransfer.convertSuccess') });
      setConvertPct(0);
      loadBalances();
    } catch (err) {
      setConvertMsg({ type: 'error', text: err.message });
    } finally {
      setConverting(false);
    }
  };

  const assets = mergeAssets(balances?.funding, balances?.trading);
  const QUICK_PCTS = [25, 50, 75, 100];

  return (
    <div className="wallet-transfer">
      <p className="settings-note">
        {t('walletTransfer.infoNote')}
      </p>

      {balanceError && <div className="msg msg-error">{balanceError}</div>}

      {/* ── Varlıklarım — Fon+İşlem birleşik, sadece "0"dan farklı olanlar ── */}
      <div className="wallet-detail">
        <div className="wallet-detail-row" style={{ marginBottom: 2 }}>
          <span className="wallet-detail-subtitle" style={{ margin: 0 }}>{t('walletTransfer.allAssetsTitle')}</span>
          <button className="wallet-refresh-btn" onClick={loadBalances} disabled={loadingBalances} title={t('walletTransfer.refreshBalances')}>↺</button>
        </div>
        {loadingBalances && <div className="settings-note">{t('walletTransfer.loading')}</div>}
        {!loadingBalances && assets.length === 0 && <div className="settings-note">—</div>}
        {!loadingBalances && assets.map(b => (
          <div className="wallet-detail-row" key={b.ccy}>
            <span>{b.ccy}</span>
            <strong>{fmtAsset(b.amount)}{b.usdValue ? ` (≈$${fmtAsset(b.usdValue)})` : ''}</strong>
          </div>
        ))}
      </div>

      <button type="button" className="wallet-detail-toggle" onClick={toggleDetail}>
        {showDetail ? t('walletTransfer.hideDetail') : t('walletTransfer.showDetail')}
      </button>

      {showDetail && (
        <div className="wallet-detail">
          {loadingDetail && <div className="settings-note">{t('walletTransfer.loading')}</div>}
          {detailError && <div className="msg msg-error">{detailError}</div>}
          {detail && !loadingDetail && (
            <>
              <div className="wallet-detail-row">
                <span>{t('walletTransfer.totalEquity')}</span><strong>{fmtUsdt(detail.trading.eq)} USDT</strong>
              </div>
              <div className="wallet-detail-row">
                <span>{t('walletTransfer.availableForOrders')}</span><strong>{fmtUsdt(detail.trading.availBal)} USDT</strong>
              </div>
              <div className="wallet-detail-row">
                <span>{t('walletTransfer.lockedInPositions')}</span><strong>{fmtUsdt(detail.trading.frozenBal)} USDT</strong>
              </div>
              <div className="wallet-detail-row">
                <span>{t('walletTransfer.lockedInPendingOrders')}</span><strong>{fmtUsdt(detail.trading.ordFrozen)} USDT</strong>
              </div>
              {detail.openPositions.length > 0 && (
                <>
                  <div className="wallet-detail-subtitle">{t('walletTransfer.openPositionsTitle')}</div>
                  {detail.openPositions.map((p, i) => (
                    <div className="wallet-detail-row" key={i}>
                      <span>{p.instId} {p.posSide} ({p.mgnMode})</span>
                      <strong>{fmtUsdt(p.margin)} USDT{p.upl ? ` · PnL ${fmtUsdt(p.upl)}` : ''}</strong>
                    </div>
                  ))}
                </>
              )}
              {detail.openPositions.length === 0 && (
                <div className="settings-note">{t('walletTransfer.noOpenPositions')}</div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── USDT ⇄ TRY Dönüştür — bir pozisyon değil, düz para birimi dönüşümü ── */}
      <p className="settings-note" style={{ marginTop: 4 }}>
        {t('walletTransfer.convertInfoNote')}
      </p>

      <div className="wallet-columns">
        <div className={`wallet-card ${isTryToUsdt ? 'wallet-card-from' : 'wallet-card-to'}`}>
          <div className="wallet-card-label">TRY</div>
          <div className="wallet-card-amount">
            {loadingBalances ? '…' : fmtAsset(tradingTry)} <span className="wallet-card-ccy">TRY</span>
          </div>
        </div>

        <div className="wallet-flow">
          <button className="wallet-flip-btn" onClick={flipConvertDirection} disabled={converting} title={t('walletTransfer.flipDirection')}>
            {isTryToUsdt ? '→' : '←'}
          </button>
        </div>

        <div className={`wallet-card ${!isTryToUsdt ? 'wallet-card-from' : 'wallet-card-to'}`}>
          <div className="wallet-card-label">USDT</div>
          <div className="wallet-card-amount">
            {loadingBalances ? '…' : fmtAsset(tradingUsdt)} <span className="wallet-card-ccy">USDT</span>
          </div>
        </div>
      </div>

      <div className="wallet-slider-row">
        <input
          type="range" className="wallet-slider" min="0" max="100" step="1"
          value={convertPct} onChange={e => setConvertPct(Number(e.target.value))}
          disabled={converting || sourceBalance <= 0}
        />
        <div className="wallet-slider-info">
          <span>%{convertPct}</span>
          <strong>{fmtAsset(convertAmount)} {isTryToUsdt ? 'TRY' : 'USDT'}</strong>
        </div>
      </div>

      <div className="wallet-slider-quick">
        {QUICK_PCTS.map(p => (
          <button
            key={p} type="button"
            className={`wallet-quick-btn${convertPct === p ? ' active' : ''}`}
            onClick={() => setConvertPct(p)}
            disabled={converting || sourceBalance <= 0}
          >
            %{p}
          </button>
        ))}
      </div>

      <button className="btn btn-start" onClick={handleConvert} disabled={converting || convertAmount <= 0}>
        {converting ? t('walletTransfer.converting') : t('walletTransfer.convertButton', { direction: isTryToUsdt ? t('walletTransfer.directionTryToUsdt') : t('walletTransfer.directionUsdtToTry') })}
      </button>

      {convertMsg && <div className={`msg ${convertMsg.type === 'ok' ? 'msg-success' : 'msg-error'}`}>{convertMsg.text}</div>}
    </div>
  );
}
